import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Switch,
  FormControlLabel,
  Paper,
  IconButton,
  Tooltip,
  Link,
  Pagination,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Language as WebsiteIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Launch as LaunchIcon,
  CloudDownload as FetchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface WebsiteStats {
  total_entries: number;
  entries_with_websites: number;
  entries_without_websites: number;
  entries_with_coordinates: number;
}

interface CategoryStats {
  category: string;
  total: number;
  with_websites: number;
}

interface BackfillResult {
  processed: number;
  found: number;
  errors: number;
  details: Array<{
    name: string;
    website?: string;
    error?: string;
  }>;
}

interface TestResult {
  found: boolean;
  website?: string;
}

interface WebsiteEntry {
  id: number;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  category: string;
  website?: string;
  popularity_score: number;
  confidence_score: number;
  created_at: string;
  updated_at: string;
}

const WebsiteManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState<WebsiteStats | null>(null);
  const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Backfill dialog state
  const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
  const [backfillCategory, setBackfillCategory] = useState('');
  const [backfillLimit, setBackfillLimit] = useState(50);
  const [skipExisting, setSkipExisting] = useState(true);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResults, setBackfillResults] = useState<BackfillResult | null>(null);
  
  // Test dialog state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testName, setTestName] = useState('');
  const [testAddress, setTestAddress] = useState('');
  const [testCategory, setTestCategory] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  
  // Website list state
  const [websites, setWebsites] = useState<WebsiteEntry[]>([]);
  const [websitesLoading, setWebsitesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterHasWebsite, setFilterHasWebsite] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<WebsiteEntry | null>(null);
  const [editWebsite, setEditWebsite] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    loadStats();
    if (activeTab === 1) {
      loadWebsites();
    }
  }, [activeTab]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/admin/websites/website-stats');
      setStats(response.data.overall_stats);
      setCategoryStats(response.data.category_breakdown || []);
    } catch (err: any) {
      console.error('Failed to load website stats:', err);
      setError(err.response?.data?.error || 'Failed to load website stats');
    } finally {
      setLoading(false);
    }
  };

  const loadWebsites = async () => {
    try {
      setWebsitesLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(filterCategory && { category: filterCategory }),
        ...(filterHasWebsite !== 'all' && { hasWebsite: filterHasWebsite }),
        ...(searchQuery && { search: searchQuery }),
      });
      
      const response = await api.get(`/admin/websites/websites?${params}`);
      setWebsites(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err: any) {
      console.error('Failed to load websites:', err);
      setError(err.response?.data?.error || 'Failed to load websites');
    } finally {
      setWebsitesLoading(false);
    }
  };

  const handleBackfill = async () => {
    try {
      setBackfillLoading(true);
      setError(null);
      
      const payload = {
        limit: backfillLimit,
        skipExisting,
        ...(backfillCategory && { category: backfillCategory })
      };
      
      const response = await api.post('/admin/websites/backfill-websites', payload);
      setBackfillResults(response.data);
      
      // Refresh stats after backfill
      await loadStats();
      if (activeTab === 1) {
        await loadWebsites();
      }
    } catch (err: any) {
      console.error('Backfill failed:', err);
      setError(err.response?.data?.error || 'Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleTestFetch = async () => {
    try {
      setTestLoading(true);
      setError(null);
      
      const response = await api.post('/admin/websites/test-website-fetch', {
        name: testName,
        address: testAddress || undefined,
        category: testCategory || undefined
      });
      
      setTestResult(response.data);
    } catch (err: any) {
      console.error('Test fetch failed:', err);
      setError(err.response?.data?.error || 'Test fetch failed');
    } finally {
      setTestLoading(false);
    }
  };

  const handleEditWebsite = (entry: WebsiteEntry) => {
    setEditingEntry(entry);
    setEditWebsite(entry.website || '');
    setEditDialogOpen(true);
  };

  const handleSaveWebsite = async () => {
    if (!editingEntry) return;
    
    try {
      setEditLoading(true);
      
      await api.put(`/admin/websites/websites/${editingEntry.id}`, {
        website: editWebsite || null
      });
      
      setEditDialogOpen(false);
      await loadWebsites();
      await loadStats();
    } catch (err: any) {
      console.error('Save website failed:', err);
      setError(err.response?.data?.error || 'Failed to save website');
    } finally {
      setEditLoading(false);
    }
  };

  const handleFetchWebsite = async (entry: WebsiteEntry) => {
    try {
      setWebsitesLoading(true);
      
      await api.post(`/admin/websites/websites/${entry.id}/fetch`);
      
      await loadWebsites();
      await loadStats();
    } catch (err: any) {
      console.error('Fetch website failed:', err);
      setError(err.response?.data?.error || 'Failed to fetch website');
    } finally {
      setWebsitesLoading(false);
    }
  };

  const resetFilters = () => {
    setFilterCategory('');
    setFilterHasWebsite('all');
    setSearchQuery('');
    setCurrentPage(1);
  };

  useEffect(() => {
    if (activeTab === 1) {
      loadWebsites();
    }
  }, [currentPage, filterCategory, filterHasWebsite, searchQuery]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WebsiteIcon />
        Website Management
      </Typography>

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Overview & Bulk Tools" />
        <Tab label="Browse & Edit Websites" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Overview Tab */}
      {activeTab === 0 && (
        <Box>
          {/* Stats Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <TrendingUpIcon sx={{ mr: 1 }} />
                  Website Coverage
                </Typography>
                
                {loading && <CircularProgress size={24} />}
                
                {stats && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Entries: {stats.total_entries.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      With Websites: {stats.entries_with_websites.toLocaleString()} 
                      ({((stats.entries_with_websites / stats.total_entries) * 100).toFixed(1)}%)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Without Websites: {stats.entries_without_websites.toLocaleString()}
                    </Typography>
                  </Box>
                )}
                
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadStats}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  Refresh Stats
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<FetchIcon />}
                    onClick={() => setBackfillDialogOpen(true)}
                    disabled={backfillLoading}
                  >
                    Bulk Fetch Websites
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={() => setTestDialogOpen(true)}
                  >
                    Test Website Fetch
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Category Breakdown */}
          {categoryStats && categoryStats.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Breakdown
                </Typography>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell>Total Entries</TableCell>
                        <TableCell>With Websites</TableCell>
                        <TableCell>Coverage</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(categoryStats || []).map((category) => (
                        <TableRow key={category.category}>
                          <TableCell>
                            <Chip 
                              label={category.category || 'Unknown'} 
                              size="small" 
                              variant="outlined" 
                            />
                          </TableCell>
                          <TableCell>{category.total.toLocaleString()}</TableCell>
                          <TableCell>{category.with_websites.toLocaleString()}</TableCell>
                          <TableCell>
                            {((category.with_websites / category.total) * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setBackfillCategory(category.category);
                                setBackfillDialogOpen(true);
                              }}
                            >
                              Backfill
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Browse & Edit Tab */}
      {activeTab === 1 && (
        <Box>
          {/* Filters */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Filters</Typography>
                
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
                  gap: 2, 
                  alignItems: 'center' 
                }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search locations"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {(categoryStats || []).map((cat) => (
                        <MenuItem key={cat.category} value={cat.category}>
                          {cat.category} ({cat.total})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth size="small">
                    <InputLabel>Website Status</InputLabel>
                    <Select
                      value={filterHasWebsite}
                      onChange={(e) => setFilterHasWebsite(e.target.value)}
                      label="Website Status"
                    >
                      <MenuItem value="all">All Entries</MenuItem>
                      <MenuItem value="true">Has Website</MenuItem>
                      <MenuItem value="false">No Website</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={resetFilters}
                    fullWidth
                  >
                    Clear Filters
                  </Button>
                </Box>
              </CardContent>
            </Card>          {/* Website List */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Website Entries</Typography>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadWebsites}
                  disabled={websitesLoading}
                >
                  Refresh
                </Button>
              </Box>

              {websitesLoading && <LinearProgress sx={{ mb: 2 }} />}

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Website</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {websites.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {entry.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={entry.category || 'Unknown'} 
                            size="small" 
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {entry.address}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {entry.website ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Link 
                                href={entry.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                sx={{ 
                                  maxWidth: 200, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block'
                                }}
                              >
                                {entry.website}
                              </Link>
                              <IconButton 
                                size="small" 
                                href={entry.website} 
                                target="_blank"
                              >
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No website
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.confidence_score ? (entry.confidence_score * 100).toFixed(0) + '%' : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit website">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditWebsite(entry)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Fetch website">
                              <IconButton 
                                size="small" 
                                onClick={() => handleFetchWebsite(entry)}
                                disabled={websitesLoading}
                              >
                                <FetchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Backfill Dialog */}
      <Dialog open={backfillDialogOpen} onClose={() => setBackfillDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Fetch Websites</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Category Filter</InputLabel>
              <Select
                value={backfillCategory}
                onChange={(e) => setBackfillCategory(e.target.value)}
                label="Category Filter"
              >
                <MenuItem value="">All Categories</MenuItem>
                {(categoryStats || []).map((category) => (
                  <MenuItem key={category.category} value={category.category}>
                    {category.category} ({category.total - category.with_websites} missing websites)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Limit"
              type="number"
              value={backfillLimit}
              onChange={(e) => setBackfillLimit(Number(e.target.value))}
              inputProps={{ min: 1, max: 100 }}
              helperText="Maximum number of entries to process (1-100)"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={skipExisting}
                  onChange={(e) => setSkipExisting(e.target.checked)}
                />
              }
              label="Skip entries that already have websites"
            />

            {backfillLoading && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Processing websites... This may take several minutes.
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {backfillResults && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    Processed: {backfillResults.processed} | Found: {backfillResults.found} | Errors: {backfillResults.errors}
                  </Typography>
                </Alert>
                
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {backfillResults.details.map((result, index) => (
                    <Alert 
                      key={index}
                      severity={result.website ? 'success' : result.error ? 'error' : 'warning'}
                      sx={{ mb: 1 }}
                    >
                      <strong>{result.name}</strong>: {
                        result.website ? `Found ${result.website}` :
                        result.error ? `Error: ${result.error}` :
                        'No website found'
                      }
                    </Alert>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackfillDialogOpen(false)}>Close</Button>
          <Button 
            onClick={handleBackfill} 
            variant="contained"
            disabled={backfillLoading}
          >
            {backfillLoading ? <CircularProgress size={20} /> : 'Start Backfill'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Test Website Fetch</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Location Name"
              value={testName}
              onChange={(e) => setTestName(e.target.value)}
              required
              fullWidth
            />
            
            <TextField
              label="Address (Optional)"
              value={testAddress}
              onChange={(e) => setTestAddress(e.target.value)}
              fullWidth
            />
            
            <TextField
              label="Category (Optional)"
              value={testCategory}
              onChange={(e) => setTestCategory(e.target.value)}
              fullWidth
            />

            {testResult && (
              <Alert severity={testResult.found ? 'success' : 'warning'}>
                {testResult.found ? (
                  <Box>
                    <Typography><strong>Website Found:</strong></Typography>
                    <Typography variant="body2">{testResult.website}</Typography>
                  </Box>
                ) : (
                  'No website found for this location'
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
          <Button 
            onClick={handleTestFetch} 
            variant="contained"
            disabled={!testName || testLoading}
          >
            {testLoading ? <CircularProgress size={20} /> : 'Test Fetch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Website Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Website</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Location: {editingEntry?.name}
            </Typography>
            
            <TextField
              label="Website URL"
              value={editWebsite}
              onChange={(e) => setEditWebsite(e.target.value)}
              fullWidth
              placeholder="https://example.com"
              helperText="Leave empty to remove website"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveWebsite} 
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebsiteManagement;